import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Typography from '../../Typography';
import {
  clampPct,
  effectiveLotPct,
  resolveUsageBand,
} from '../../../utils/constants/creditUsageThresholds';

/**
 * ROAR-4943 — one credit lot's block (AC1, AC2), matching the Figma "Account Credits" frame.
 *
 * Purely presentational: it takes a lot and resolves its OWN band, which is what makes the per-lot
 * colouring of ROAR-4899 §4 decision 7 fall out for free — a nearly-exhausted grant reads red even
 * when its siblings are healthy.
 *
 * `pct` is backend-computed, so nothing here divides `used` by `allotment`. A 0-allotment lot
 * arrives as `pct: 0` and renders an empty default-band bar; that guard lives here rather than in
 * the band table because `resolveUsageBand` takes only `pct` (decision 7).
 *
 * AC2 words the carry-over sentence unconditionally ("unused credits don't carry over"), which is
 * true of `subscription` lots only. It is rendered from the lot's own `carries_over` instead — a
 * deliberate, PM-visible deviation (decision 14).
 *
 * **ROAR-4962 decided NOT to surface `lot.remaining`**, which the contract also returns. It is
 * derivable from what is already on screen — the backend computes `used` as
 * `max(0, allotment - remaining)`, so `remaining` is `allotment - used` for every lot that is not
 * clamped — and a third number next to "158 /500 credits used" invites the reader to reconcile
 * three figures where two suffice. The spendable total the user actually needs is
 * `balance.available`, which the pane renders once for the whole account rather than per lot;
 * a per-lot `remaining` is not that number and would be easy to mistake for it.
 */
const CreditLotSummary = ({ lot }) => {
  // `effectiveLotPct` carries the `allotment === 0` guard of §4 decision 7. The pane's banner reads
  // a lot's percentage through the same helper, so the bar and the banner cannot disagree.
  const pct = effectiveLotPct(lot);
  const band = resolveUsageBand(pct);
  const width = clampPct(pct);

  // A `manual` grant may have no expiry at all. Dropping the clause beats rendering
  // "Resets Invalid date".
  const resetDate = lot?.resets_at ? moment(lot.resets_at).format('MMM D, YYYY') : null;
  const metaLine = [
    lot?.label,
    resetDate ? `Resets ${resetDate}` : null,
    lot?.carries_over ? 'does carry over' : 'does not carry over',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={'mb-4 last:mb-0'}>
      <Typography variant={'body2'} className={'text-body'}>
        {metaLine}
      </Typography>

      <div className={'mt-2 flex items-end justify-between'}>
        <Typography variant={'body1'} className={'text-headings'}>
          {/* Defaulted like every other field here: a partial lot must read "0 /0 credits used",
              never "undefined/undefined credits used". */}
          <span className={'text-lg font-semibold'}>{lot?.used ?? 0}</span>
          <span>{`/${lot?.allotment ?? 0} credits used`}</span>
        </Typography>
        <Typography variant={'body1'} className={'font-semibold text-headings'}>
          {`${width}%`}
        </Typography>
      </div>

      <div className={'mt-1 h-1.5 w-full rounded bg-grey3'}>
        <div
          data-testid={'credit-lot-bar'}
          className={`h-1.5 rounded ${band.barClassName}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

CreditLotSummary.propTypes = {
  lot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    source: PropTypes.string,
    resets_at: PropTypes.string,
    carries_over: PropTypes.bool,
    used: PropTypes.number,
    allotment: PropTypes.number,
    pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

export default CreditLotSummary;
